/*
 * Nalzea storefront behaviour: header (announcements, mega menus, drawer,
 * search, locale), wishlist ("coups de cœur"), welcome offer on bags,
 * scroll reveal and parallax. No dependencies; strings and settings come from
 * #nz-config.
 */

const config = (() => {
  try {
    return JSON.parse(document.getElementById('nz-config')?.textContent || '{}');
  } catch {
    return {};
  }
})();
const strings = config.strings || {};
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const designMode = Boolean(window.Shopify && window.Shopify.designMode);

/* ---------- Helpers ---------- */

export function formatMoney(cents, format = config.moneyFormat || '{{amount_with_comma_separator}} €') {
  const value = Number(cents || 0) / 100;
  const group = (n, thousands, decimal, places) => {
    const [int, dec] = n.toFixed(places).split('.');
    const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return dec ? `${withSep}${decimal}${dec}` : withSep;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(value, ',', '.', 0);
      case 'amount_with_comma_separator':
        return group(value, '.', ',', 2);
      case 'amount_no_decimals_with_comma_separator':
        return group(value, '.', ',', 0);
      case 'amount_with_apostrophe_separator':
        return group(value, "'", '.', 2);
      case 'amount_with_space_separator':
        return group(value, ' ', ',', 2);
      default:
        return group(value, ',', '.', 2);
    }
  });
}

let toastTimer;
export function toast(message) {
  if (!message) return;
  let el = document.querySelector('.nz-toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'nz-toast';
    el.setAttribute('role', 'status');
    document.body.append(el);
  }
  el.textContent = message;
  requestAnimationFrame(() => el.classList.add('is-visible'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
}

/* ---------- Header ---------- */

class NzHeader extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.resizeObserver = new ResizeObserver(() => this.#setHeights());
    this.resizeObserver.observe(this);

    // Horizon scrolls .page-wrapper on desktop (the window stays at 0), the document on phones.
    const pageWrapper = document.querySelector('.page-wrapper');
    const onScroll = () => {
      const top = Math.max(window.scrollY, pageWrapper ? pageWrapper.scrollTop : 0);
      this.toggleAttribute('data-scrolled', top > 4);
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    pageWrapper?.addEventListener('scroll', onScroll, { passive: true, signal });
    onScroll();

    this.#initAnnouncements(signal);
    this.#initMegaMenus(signal);
    this.#initDrawer(signal);

    this.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[data-nz-search]')) return;
        const modal = document.getElementById('search-modal');
        if (modal && typeof modal.showDialog === 'function') {
          this.querySelector('dialog[open]')?.close();
          modal.showDialog();
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}search`;
        }
      },
      { signal }
    );

    this.querySelector('#NzLocalizationForm')?.addEventListener(
      'change',
      (event) => event.currentTarget instanceof HTMLFormElement && event.currentTarget.submit(),
      { signal }
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    clearInterval(this.announceTimer);
  }

  #setHeights() {
    const height = Math.round(this.getBoundingClientRect().height);
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${height}px`);
  }

  #initAnnouncements(signal) {
    const root = this.querySelector('[data-nz-announce]');
    if (!root) return;
    const slides = [...root.querySelectorAll('[data-slide]')];
    const toggle = root.querySelector('[data-nz-announce-toggle]');
    if (slides.length < 2) return;

    let index = 0;
    let paused = false;
    let hovered = false;
    const show = (next) => {
      slides[index].hidden = true;
      index = (next + slides.length) % slides.length;
      slides[index].hidden = false;
    };
    const speed = Number(this.dataset.speed) || 5000;
    this.announceTimer = setInterval(() => {
      if (!paused && !hovered && !document.hidden) show(index + 1);
    }, speed);

    root.addEventListener('pointerenter', () => (hovered = true), { signal });
    root.addEventListener('pointerleave', () => (hovered = false), { signal });
    root.addEventListener('focusin', () => (hovered = true), { signal });
    root.addEventListener('focusout', () => (hovered = false), { signal });
    toggle?.addEventListener(
      'click',
      () => {
        paused = !paused;
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      },
      { signal }
    );
  }

  #initMegaMenus(signal) {
    const items = [...this.querySelectorAll('[data-nz-mega-item]')];
    let openTimer;
    let closeTimer;

    const open = (item) => {
      items.forEach((other) => other !== item && close(other, true));
      const panel = item.querySelector('[data-nz-mega]');
      if (!panel || item.classList.contains('is-open')) return;
      item.classList.add('is-open');
      item.querySelector('[data-nz-mega-toggle]')?.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      requestAnimationFrame(() => panel.classList.add('is-open'));
    };
    const close = (item, instant = false) => {
      const panel = item.querySelector('[data-nz-mega]');
      if (!panel || !item.classList.contains('is-open')) return;
      item.classList.remove('is-open');
      item.querySelector('[data-nz-mega-toggle]')?.setAttribute('aria-expanded', 'false');
      panel.classList.remove('is-open');
      if (instant || reducedMotion.matches) panel.hidden = true;
      else setTimeout(() => !item.classList.contains('is-open') && (panel.hidden = true), 250);
    };

    for (const item of items) {
      item.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(closeTimer);
          clearTimeout(openTimer);
          openTimer = setTimeout(() => open(item), 70);
        },
        { signal }
      );
      item.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(openTimer);
          closeTimer = setTimeout(() => close(item), 140);
        },
        { signal }
      );
      item.querySelector('[data-nz-mega-toggle]')?.addEventListener(
        'click',
        () => (item.classList.contains('is-open') ? close(item) : open(item)),
        { signal }
      );
      item.addEventListener(
        'focusout',
        (event) => {
          if (!(event.relatedTarget instanceof Node) || !item.contains(event.relatedTarget)) close(item);
        },
        { signal }
      );
    }

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const current = items.find((item) => item.classList.contains('is-open'));
        if (!current) return;
        close(current);
        current.querySelector('.nz-nav__link')?.focus();
      },
      { signal }
    );
  }

  #initDrawer(signal) {
    const drawer = this.querySelector('[data-nz-drawer]');
    if (!(drawer instanceof HTMLDialogElement)) return;
    const panels = [...drawer.querySelectorAll('.nz-drawer__panel')];
    const showPanel = (panel) => {
      for (const other of panels) {
        const current = other === panel;
        other.classList.toggle('is-current', current);
        if (current) other.hidden = false;
        else if (other.dataset.nzPanel !== 'root') setTimeout(() => !other.classList.contains('is-current') && (other.hidden = true), 400);
      }
      panel.querySelector('button, a')?.focus();
    };

    this.querySelector('[data-nz-drawer-open]')?.addEventListener(
      'click',
      () => {
        showPanel(panels[0]);
        drawer.showModal();
        document.documentElement.style.overflow = 'hidden';
      },
      { signal }
    );
    drawer.addEventListener('close', () => (document.documentElement.style.overflow = ''), { signal });
    drawer.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (target === drawer) return drawer.close();
        if (!(target instanceof Element)) return;
        if (target.closest('[data-nz-drawer-close]')) return drawer.close();
        const opener = target.closest('[data-nz-panel-open]');
        if (opener instanceof HTMLElement) {
          const panel = drawer.querySelector(`#${opener.dataset.nzPanelOpen}`);
          if (panel) showPanel(panel);
          return;
        }
        if (target.closest('[data-nz-panel-back]')) showPanel(panels[0]);
      },
      { signal }
    );
  }
}

if (!customElements.get('nz-header')) customElements.define('nz-header', NzHeader);

/* ---------- Wishlist ("coups de cœur") ---------- */

const WISHLIST_KEY = 'nz:wishlist:v1';

export const wishlist = {
  read() {
    try {
      const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string') : [];
    } catch {
      return [];
    }
  },
  write(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list.slice(0, 100)));
    } catch {
      /* storage can be unavailable (private mode); hearts still toggle for this page */
    }
    this.sync();
    document.dispatchEvent(new CustomEvent('nz:wishlist-change', { detail: { list } }));
  },
  has(handle) {
    return this.read().includes(handle);
  },
  toggle(handle) {
    const list = this.read();
    const exists = list.includes(handle);
    this.write(exists ? list.filter((item) => item !== handle) : [handle, ...list]);
    return !exists;
  },
  sync() {
    const list = this.read();
    for (const button of document.querySelectorAll('[data-nz-wish]')) {
      if (button instanceof HTMLElement) button.setAttribute('aria-pressed', String(list.includes(button.dataset.nzWish || '')));
    }
    for (const count of document.querySelectorAll('[data-nz-wishlist-count]')) {
      if (!(count instanceof HTMLElement)) continue;
      count.textContent = list.length > 99 ? '99+' : String(list.length);
      count.hidden = list.length === 0;
    }
  },
};

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-nz-wish]') : null;
  if (!(button instanceof HTMLElement)) return;
  event.preventDefault();
  event.stopPropagation();
  const added = wishlist.toggle(button.dataset.nzWish || '');
  toast(added ? strings.wishlistSaved : strings.wishlistRemoved);
});

window.addEventListener('storage', (event) => event.key === WISHLIST_KEY && wishlist.sync());

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

function sizedImage(src, width) {
  if (!src) return '';
  const url = new URL(src.startsWith('//') ? `https:${src}` : src, window.location.origin);
  url.searchParams.set('width', String(width));
  return url.toString();
}

class NzWishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-nz-wl-grid]');
    this.empty = this.querySelector('[data-nz-wl-empty]');
    this.count = this.closest('.nz-wl')?.querySelector('[data-nz-wl-count]');
    this.share = this.closest('.nz-wl')?.querySelector('[data-nz-wl-share]');
    const shared = new URLSearchParams(window.location.search).get('items');
    this.sharedHandles = shared ? shared.split(',').filter(Boolean).slice(0, 50) : null;

    this.onChange = () => !this.sharedHandles && this.render();
    document.addEventListener('nz:wishlist-change', this.onChange);
    this.share?.addEventListener('click', () => this.copyLink());
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('nz:wishlist-change', this.onChange);
  }

  get handles() {
    return this.sharedHandles || wishlist.read();
  }

  async render() {
    const handles = this.handles;
    const root = window.Shopify?.routes?.root || '/';
    const products = (
      await Promise.all(
        handles.map((handle) =>
          fetch(`${root}products/${encodeURIComponent(handle)}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        )
      )
    ).filter(Boolean);

    if (this.grid) this.grid.innerHTML = products.map((product) => this.card(product)).join('');
    if (this.empty) this.empty.hidden = products.length > 0;
    if (this.share) this.share.hidden = products.length === 0;
    if (this.count) {
      const template = products.length === 1 ? this.dataset.countOne : this.dataset.countOther;
      this.count.textContent = products.length ? String(template || '').replace('[count]', String(products.length)) : '';
    }
    wishlist.sync();
  }

  card(product) {
    const variants = product.variants || [];
    const available = variants.filter((variant) => variant.available);
    const url = product.url || `/products/${product.handle}`;
    const image = product.featured_image
      ? `<img src="${escapeHtml(sizedImage(product.featured_image, 600))}" srcset="${escapeHtml(sizedImage(product.featured_image, 400))} 400w, ${escapeHtml(sizedImage(product.featured_image, 800))} 800w" sizes="(min-width: 1100px) 25vw, 50vw" loading="lazy" alt="${escapeHtml(product.title)}">`
      : '';
    const compare =
      product.compare_at_price > product.price ? `<s>${escapeHtml(formatMoney(product.compare_at_price))}</s>` : '';
    let cta;
    if (!available.length) {
      cta = `<span class="button-secondary nz-wl__cta" aria-disabled="true">${escapeHtml(this.dataset.soldOut)}</span>`;
    } else if (variants.length === 1) {
      cta = `<button type="button" class="button nz-wl__cta" data-nz-quick-add="${available[0].id}">${escapeHtml(this.dataset.add)}</button>`;
    } else {
      cta = `<a href="${escapeHtml(url)}" class="button-secondary nz-wl__cta">${escapeHtml(this.dataset.choose)}</a>`;
    }
    return `<li class="nz-wl__item">
      <a href="${escapeHtml(url)}" class="nz-wl__media nz-tile">${image}</a>
      <button type="button" class="nz-wish" data-nz-wish="${escapeHtml(product.handle)}" aria-pressed="false" aria-label="${escapeHtml(this.dataset.remove)} ${escapeHtml(product.title)}">
        <svg class="nz-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M12 20.3s-7.8-4.6-7.8-10.4A4.4 4.4 0 0 1 12 7.1a4.4 4.4 0 0 1 7.8 2.8c0 5.8-7.8 10.4-7.8 10.4Z"/></svg>
      </button>
      <a href="${escapeHtml(url)}" class="nz-wl__name">${escapeHtml(product.title)}</a>
      <span class="nz-wl__price">${escapeHtml(formatMoney(product.price))}${compare}</span>
      ${cta}
    </li>`;
  }

  async copyLink() {
    const url = new URL(window.location.href);
    url.searchParams.set('items', this.handles.join(','));
    try {
      await navigator.clipboard.writeText(url.toString());
      toast(strings.copied);
    } catch {
      window.prompt('', url.toString());
    }
  }
}

if (!customElements.get('nz-wishlist-page')) customElements.define('nz-wishlist-page', NzWishlistPage);
document.addEventListener('shopify:section:load', () => wishlist.sync());

/* ---------- One-tap add to cart (cart picks, wishlist) ---------- */

export async function addToCart(variantId, quantity = 1) {
  const actions = window.Shopify?.actions;
  if (actions?.updateCart) {
    const result = await actions.updateCart(
      { lines: [{ merchandiseId: String(variantId), quantity }] },
      { event: { context: 'standard-action' } }
    );
    if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
    await actions.openCart?.();
    return;
  }
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
  });
  if (!response.ok) throw new Error((await response.json().catch(() => ({}))).description || 'Cart error');
  window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-nz-quick-add]') : null;
  if (!(button instanceof HTMLElement) || button.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();
  button.setAttribute('aria-busy', 'true');
  try {
    await addToCart(button.dataset.nzQuickAdd);
    toast(strings.added);
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error));
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/* ---------- Product cards: "+" opens the sizes on touch screens ---------- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const toggle = target?.closest('[data-nz-card-sizes-toggle]');
  for (const open of document.querySelectorAll('.nz-card-sizes[data-open]')) {
    if (toggle && open.previousElementSibling === toggle) continue;
    if (!target?.closest('.nz-card-sizes') || !open.contains(target)) {
      open.removeAttribute('data-open');
      open.previousElementSibling?.setAttribute('aria-expanded', 'false');
    }
  }
  if (!toggle) return;
  event.preventDefault();
  const sizes = toggle.nextElementSibling;
  if (!(sizes instanceof HTMLElement)) return;
  const opening = !sizes.hasAttribute('data-open');
  sizes.toggleAttribute('data-open', opening);
  toggle.setAttribute('aria-expanded', String(opening));
});

/* ---------- Cart suggestions: a chosen size arms "Ajouter au panier" ---------- */

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-nz-pick-size]')) return;
  const button = select.closest('.nz-pick')?.querySelector('[data-nz-pick-add]');
  if (!(button instanceof HTMLButtonElement)) return;
  button.dataset.nzQuickAdd = select.value;
  button.disabled = !select.value;
});

/* ---------- Lists that apply on change (country in the phone menu) ---------- */

document.addEventListener('change', (event) => {
  const select = event.target;
  if (select instanceof HTMLSelectElement && select.matches('[data-nz-autosubmit]')) select.form?.requestSubmit();
});

/* ---------- Dialogs (size guide…) ---------- */

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const opener = event.target.closest('[data-nz-dialog-open]');
  if (opener instanceof HTMLElement) {
    const dialog = document.getElementById(opener.dataset.nzDialogOpen || '');
    if (dialog instanceof HTMLDialogElement) {
      dialog.showModal();
      dialog.addEventListener('close', () => opener.focus(), { once: true });
    }
    return;
  }
  const dialog = event.target.closest('dialog.nz-dialog');
  if (dialog instanceof HTMLDialogElement && (event.target === dialog || event.target.closest('[data-nz-dialog-close]'))) {
    dialog.close();
  }
});

/* ---------- Estimated delivery dates ---------- */

function addBusinessDays(start, days) {
  const date = new Date(start);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date;
}

class NzEta extends HTMLElement {
  connectedCallback() {
    const min = Number(this.dataset.min) || 7;
    const max = Math.max(Number(this.dataset.max) || 10, min);
    const processing = Number(this.dataset.processing || 0);
    const cutoff = Number(this.dataset.cutoff) || 14;
    const now = new Date();
    const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;
    // Order date: today before the cut-off on a weekday, otherwise the next business day;
    // then the preparation time, then the carrier's min/max business days.
    let start = new Date(now);
    if (isWeekend(start) || now.getHours() >= cutoff) {
      do start.setDate(start.getDate() + 1);
      while (isWeekend(start));
    }
    if (processing > 0) start = addBusinessDays(start, processing);
    const format = new Intl.DateTimeFormat(config.locale || document.documentElement.lang || 'fr', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    const text = (strings.eta || '[from] – [to]')
      .replace('[from]', format.format(addBusinessDays(start, min)))
      .replace('[to]', format.format(addBusinessDays(start, max)));
    const target = this.querySelector('[data-nz-eta-text]');
    if (target) target.textContent = text;
  }
}

if (!customElements.get('nz-eta')) customElements.define('nz-eta', NzEta);

/* ---------- Tabs ---------- */

class NzTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.addEventListener('click', (event) => {
      const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
      if (tab) this.select(this.tabs.indexOf(tab));
    });
    this.addEventListener('keydown', (event) => {
      const index = this.tabs.indexOf(document.activeElement);
      if (index < 0) return;
      const keys = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: this.tabs.length - 1 - index };
      if (!(event.key in keys)) return;
      event.preventDefault();
      this.select((index + keys[event.key] + this.tabs.length) % this.tabs.length, true);
    });
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !selected;
    });
    this.style.setProperty('--nz-tab', String(index));
    if (focus) this.tabs[index]?.focus();
  }
}

if (!customElements.get('nz-tabs')) customElements.define('nz-tabs', NzTabs);

/* ---------- Colourways: other colours of the same model ---------- */

const shopRoot = () => window.Shopify?.routes?.root || '/';

/* Same rules as blocks/nz-colorways.liquid: fabric words are ignored, glitter words add sparkle. */
const COLOR_SKIP = new Set(['a', 'fines', 'rayures', 'pinstripe', 'lace', 'dentelle', 'embellishment', 'brode', 'brodee', 'satin', 'satine', 'print', 'imprime', 'velours']);
const COLOR_SPARKLE = new Set(['scintillant', 'scintillante', 'sparkle', 'paillete', 'pailletee', 'paillettes', 'glitter', 'metallise', 'metallisee']);

function handleize(value) {
  return String(value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function colorDot(name, map) {
  let sparkle = false;
  const base = handleize(name)
    .split('-')
    .filter((word) => {
      if (COLOR_SPARKLE.has(word)) sparkle = true;
      return word && !COLOR_SPARKLE.has(word) && !COLOR_SKIP.has(word);
    })
    .join('-');
  return { hex: map.get(base) || map.get(base.split('-')[0]) || '', sparkle };
}

class NzColorways extends HTMLElement {
  async connectedCallback() {
    const model = this.dataset.model || '';
    const separator = this.dataset.separator || ' — ';
    const list = this.querySelector('[data-nz-colorways-list]');
    if (!model || !list || this.dataset.loaded) return;
    this.dataset.loaded = '1';
    const map = new Map(
      String(this.dataset.colors || '')
        .split(',')
        .map((pair) => pair.split(':').map((part) => part.trim()))
        .filter((pair) => pair.length === 2 && /^#[0-9a-f]{3,8}$/i.test(pair[1]))
    );
    try {
      const url = new URL(`${shopRoot()}search/suggest.json`, window.location.origin);
      url.searchParams.set('q', model);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][fields]', 'title');
      const response = await fetch(url);
      if (!response.ok) return;
      const products = (await response.json())?.resources?.results?.products || [];
      const prefix = `${model}${separator}`.toLowerCase();
      const others = products.filter(
        (product) => product.handle !== this.dataset.current && String(product.title).toLowerCase().startsWith(prefix)
      );
      for (const product of others) {
        const color = String(product.title).slice(prefix.length).trim();
        const { hex, sparkle } = colorDot(color, map);
        const onSale = Number(product.compare_at_price_max) > Number(product.price);
        const image = product.featured_image?.url || product.image;
        const fill = hex
          ? ''
          : image
            ? `<img src="${escapeHtml(sizedImage(image, 72))}" width="24" height="24" alt="" loading="lazy">`
            : '';
        const item = document.createElement('li');
        item.innerHTML = `<a class="nz-colorways__swatch${sparkle ? ' is-sparkle' : ''}${onSale ? ' is-sale' : ''}" href="${escapeHtml(product.url)}" data-color="${escapeHtml(color)}"><span class="nz-colorways__dot"${
          hex ? ` style="--nz-dot: ${escapeHtml(hex)};"` : ''
        }>${fill}</span><span class="visually-hidden">${escapeHtml(color)}</span></a>`;
        list.append(item);
      }
    } catch {
      /* the current colour stays visible on its own */
    }
  }
}

if (!customElements.get('nz-colorways')) customElements.define('nz-colorways', NzColorways);

/* ---------- Recently viewed ---------- */

const RECENT_KEY = 'nz:recent:v1';

function readRecent() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
}

function trackRecent() {
  const tracker = document.querySelector('[data-nz-recent-track]');
  if (!(tracker instanceof HTMLElement)) return;
  const handle = tracker.dataset.nzRecentTrack;
  if (!handle) return;
  try {
    const list = [handle, ...readRecent().filter((item) => item !== handle)].slice(0, 12);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable */
  }
}

class NzRecent extends HTMLElement {
  async connectedCallback() {
    const exclude = this.dataset.exclude || '';
    const limit = Number(this.dataset.limit) || 8;
    const handles = readRecent().filter((handle) => handle !== exclude).slice(0, limit);
    const list = this.querySelector('[data-nz-recent-list]');
    if (!handles.length || !list) return;
    const products = (
      await Promise.all(
        handles.map((handle) =>
          fetch(`${shopRoot()}products/${encodeURIComponent(handle)}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        )
      )
    ).filter(Boolean);
    if (!products.length) return;
    list.innerHTML = products
      .map((product) => {
        const url = product.url || `/products/${product.handle}`;
        const image = product.featured_image
          ? `<img src="${escapeHtml(sizedImage(product.featured_image, 500))}" srcset="${escapeHtml(sizedImage(product.featured_image, 300))} 300w, ${escapeHtml(sizedImage(product.featured_image, 600))} 600w" sizes="(min-width: 990px) 18vw, 42vw" loading="lazy" alt="${escapeHtml(product.title)}">`
          : '';
        const compare =
          product.compare_at_price > product.price ? ` <s>${escapeHtml(formatMoney(product.compare_at_price))}</s>` : '';
        return `<li class="nz-recent__item"><a href="${escapeHtml(url)}" class="nz-recent__media nz-tile">${image}</a><a href="${escapeHtml(url)}" class="nz-recent__title">${escapeHtml(product.title)}</a><span class="nz-recent__price">${escapeHtml(formatMoney(product.price))}${compare}</span></li>`;
      })
      .join('');
    this.hidden = false;
  }
}

if (!customElements.get('nz-recent')) customElements.define('nz-recent', NzRecent);

/* ---------- Reviews (SKIMS-style list: sort, show more, relative dates) ---------- */

class NzReviews extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-nz-reviews-list]');
    this.moreButton = this.querySelector('[data-nz-reviews-more]');
    this.sortSelect = this.querySelector('[data-nz-reviews-sort]');
    this.step = Number(this.dataset.step) || 5;
    this.shown = this.step;
    this.relativeDates();
    this.sort('recent');
    this.render();
    this.moreButton?.addEventListener('click', () => {
      this.shown += this.step;
      this.render();
    });
    this.sortSelect?.addEventListener('change', () => {
      this.sort(this.sortSelect.value);
      this.shown = this.step;
      this.render();
    });
    // Back from the contact form (sent or with errors): show the outcome in the dialog.
    if (this.querySelector('[data-nz-review-posted], [data-nz-review-errors]')) {
      const dialog = this.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
    }
  }

  get items() {
    return this.list ? [...this.list.children].filter((item) => item instanceof HTMLElement) : [];
  }

  sort(mode) {
    if (!this.list) return;
    const byDate = (a, b) => (b.dataset.date || '').localeCompare(a.dataset.date || '');
    const byRating = (a, b) => Number(a.dataset.rating) - Number(b.dataset.rating);
    const items = this.items.sort((a, b) => {
      if (mode === 'high') return byRating(b, a) || byDate(a, b);
      if (mode === 'low') return byRating(a, b) || byDate(a, b);
      return byDate(a, b);
    });
    this.list.append(...items);
  }

  render() {
    this.items.forEach((item, index) => {
      item.hidden = index >= this.shown;
    });
    if (this.moreButton) this.moreButton.hidden = this.items.length <= this.shown;
  }

  relativeDates() {
    if (!('RelativeTimeFormat' in Intl)) return;
    const format = new Intl.RelativeTimeFormat(config.locale || document.documentElement.lang || 'fr', { numeric: 'auto' });
    const now = Date.now();
    for (const time of this.querySelectorAll('time[datetime]')) {
      const days = Math.round((Date.parse(time.dateTime) - now) / 86400000);
      if (Number.isNaN(days)) continue;
      const size = Math.abs(days);
      if (size < 7) time.textContent = format.format(days, 'day');
      else if (size < 30) time.textContent = format.format(Math.round(days / 7), 'week');
      else if (size < 365) time.textContent = format.format(Math.round(days / 30), 'month');
      else time.textContent = format.format(Math.round(days / 365), 'year');
    }
  }
}

if (!customElements.get('nz-reviews')) customElements.define('nz-reviews', NzReviews);

/* ---------- Newsletter teaser pill + dialog ---------- */

const TEASER_KEY = 'nz:teaser-dismissed';

class NzTeaser extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    const success = this.querySelector('.nz-newsletter__success');
    if (success) {
      this.#remember(365);
      this.hidden = false;
      this.dialog?.showModal();
    } else if (!this.#dismissed() && !designMode) {
      this.showTimer = setTimeout(() => (this.hidden = false), 6000);
    }

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-nz-teaser-open]')) this.dialog?.showModal();
      if (event.target.closest('[data-nz-teaser-dismiss]')) {
        this.#remember(Number(this.dataset.days) || 14);
        this.hidden = true;
      }
      if (event.target.closest('[data-nz-teaser-close]') || event.target === this.dialog) this.dialog?.close();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.showTimer);
  }

  #dismissed() {
    try {
      return Number(localStorage.getItem(TEASER_KEY) || 0) > Date.now();
    } catch {
      return false;
    }
  }

  #remember(days) {
    try {
      localStorage.setItem(TEASER_KEY, String(Date.now() + days * 864e5));
    } catch {
      /* ignore */
    }
  }
}

if (!customElements.get('nz-teaser')) customElements.define('nz-teaser', NzTeaser);

/* ---------- Welcome offers: down jackets and bags, for a few hours per shopper ---------- */

// One clock for both offers. It starts the first time the shopper sees an
// offer (any <nz-offer>) and is kept in this browser, unless the theme
// settings give an end date, the same for everyone. Once it has run out the
// offers are over for good: the page head hides them (html.nz-offer-over) and
// their codes leave the cart. Only offers with a code touch the cart.
const offer = config.offer || {};
const OFFER_KEY = 'nz:offer:v2';
const OFFER_MS = (Number(offer.hours) || 24) * 36e5;
const OFFER_FIXED_END = Date.parse(offer.endsAt || '') || null;
const offers = (offer.offers || []).filter((item) => item.code && item.vendor);

/** When the shopper's offer ends; `start` starts the clock on first sight. Null without a clock. */
function offerEnd(start = false) {
  if (OFFER_FIXED_END) return OFFER_FIXED_END;
  try {
    let saved = JSON.parse(localStorage.getItem(OFFER_KEY) || 'null');
    if (!saved && start) {
      saved = { start: Date.now() };
      localStorage.setItem(OFFER_KEY, JSON.stringify(saved));
    }
    return saved ? saved.start + OFFER_MS : null;
  } catch {
    return null; // Storage blocked: no clock can be kept, so no offer.
  }
}

function offerRunning() {
  const end = offerEnd();
  return Boolean(offer.enabled && offers.length && end && Date.now() < end);
}

const sameCode = (a, b) => String(a).toUpperCase() === String(b).toUpperCase();

/**
 * Puts each offer's code on the cart while the shopper's offers run and the
 * cart holds a product of that offer's brand (down jackets, bags), and takes
 * it off otherwise. Shopify applies the percentage to those products; the
 * checkout bridge carries the codes to Amboras.
 */
async function reconcileOffer() {
  if (!offers.length) return;
  const cart = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const codes = (cart.discount_codes || []).map((discount) => discount.code);
  const next = codes.filter((code) => !offers.some((item) => sameCode(item.code, code)));
  for (const item of offers) {
    const inCart = (cart.items || []).some((line) => sameCode(line.vendor, item.vendor));
    if (inCart && offerRunning()) next.push(item.code);
  }
  const same = next.length === codes.length && next.every((code) => codes.some((c) => sameCode(c, code)));
  if (same) return;
  const actions = window.Shopify?.actions;
  if (actions?.updateCart) {
    // Emits the cart events the drawer and the cart page re-render on
    await actions.updateCart({ discountCodes: next }, { event: { context: 'standard-action' } });
  } else {
    await fetch(`${shopRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: next.join(',') }),
    });
  }
}

let offerQueue = Promise.resolve();
function syncOffer() {
  offerQueue = offerQueue.then(reconcileOffer).catch((error) => console.warn('[nalzea] welcome offer:', error));
  return offerQueue;
}
// The checkout bridge waits for the cart's codes to be right before copying it.
window.nzOffer = { sync: syncOffer };

let offerOver = false;
function endOffer() {
  if (offerOver) return;
  offerOver = true;
  document.documentElement.classList.add('nz-offer-over');
  syncOffer();
}

class NzOffer extends HTMLElement {
  connectedCallback() {
    this.end = offerEnd(true);
    if (!this.end || Date.now() >= this.end) {
      endOffer();
      return;
    }
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
    // After the bar's arrival glide (nz-offer.liquid), it moves on linearly each second
    this.live = setTimeout(() => this.setAttribute('data-live', ''), 1300);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    clearTimeout(this.live);
  }

  tick() {
    const left = this.end - Date.now();
    if (left <= 0) {
      clearInterval(this.timer);
      endOffer();
      return;
    }
    const seconds = Math.floor(left / 1000);
    const parts = { h: Math.floor(seconds / 3600), m: Math.floor(seconds / 60) % 60, s: seconds % 60 };
    // Queried each second: a cart re-render may swap these nodes
    for (const el of this.querySelectorAll('[data-unit]')) {
      const value = String(parts[el.dataset.unit] ?? 0).padStart(2, '0');
      if (el.dataset.v === value) continue;
      // A changed value rolls in on its drum (nz-offer.liquid); the first one just appears
      if (/^\d+$/.test(el.dataset.v || '')) {
        el.dataset.prev = el.dataset.v;
        el.dataset.flip = el.dataset.flip === 'a' ? 'b' : 'a';
      }
      el.dataset.v = value;
    }
    this.style.setProperty('--nz-offer-progress', Math.max(0, 1 - left / OFFER_MS).toFixed(4));
  }
}

if (!customElements.get('nz-offer')) customElements.define('nz-offer', NzOffer);

document.addEventListener('shopify:cart:lines-update', (event) => {
  Promise.resolve(event.promise).then(syncOffer, () => {});
});

/**
 * Prices with the offer (snippets/nz-offer-price.liquid) are the offer seen: they
 * start the shopper's clock like an <nz-offer>, and go when it runs out.
 */
function watchOfferPrices() {
  if (!offer.enabled || !offers.length || !document.querySelector('.nz-offer-price')) return;
  const end = offerEnd(true);
  if (!end || Date.now() >= end) {
    endOffer();
    return;
  }
  setTimeout(endOffer, Math.min(end - Date.now(), 2147483647));
}

/* ---------- Product page: the shopper picks her size ---------- */

// Horizon preselects the first size, so a quick tap on "Add to cart" adds an
// XS (audit 2026-10-27). On a product page opened without ?variant=, no size
// is selected and the button says to choose one until she does; the variant
// picker then re-renders the buttons for that size. The bar that follows the
// scroll takes her back to the sizes meanwhile.
function requireSize() {
  if (designMode || new URLSearchParams(location.search).has('variant')) return;
  const form = document.querySelector('product-form-component');
  const picker = form?.closest('.shopify-section')?.querySelector('variant-picker');
  const fieldsets = picker ? picker.querySelectorAll('fieldset') : [];
  if (fieldsets.length !== 1) return;
  const inputs = [...fieldsets[0].querySelectorAll('input[type="radio"]')];
  const button = form.querySelector('[ref="addToCartButton"]');
  if (inputs.length < 2 || !button || button.disabled) return;

  for (const input of inputs) {
    input.checked = false;
    input.dataset.currentChecked = 'false';
  }
  // Horizon parks the dark pill of each unselected size outside it; with none
  // checked every size would be filled dark, so they wait outside (nz.css)
  fieldsets[0].setAttribute('data-nz-size-pending', '');
  fieldsets[0].addEventListener('change', () => fieldsets[0].removeAttribute('data-nz-size-pending'), { once: true });
  button.disabled = true;
  const label = [...button.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
  if (label && config.strings?.chooseSize) label.textContent = config.strings.chooseSize;

  document.querySelector('sticky-add-to-cart [ref="addToCartButton"]')?.addEventListener(
    'click',
    (event) => {
      const current = form.querySelector('[ref="addToCartButton"]');
      if (!current?.disabled || inputs.some((input) => input.isConnected && input.checked)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const sizes = form.closest('.shopify-section')?.querySelector('variant-picker fieldset') || fieldsets[0];
      const box = sizes.getBoundingClientRect();
      window.scrollTo({
        top: Math.max(0, window.scrollY + box.top - (window.innerHeight - box.height) / 2),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    },
    { capture: true }
  );
}

/* ---------- Scroll reveal + parallax ---------- */

function initReveal() {
  if (designMode || reducedMotion.matches || !('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  for (const section of document.querySelectorAll('main .shopify-section, .nz-footer')) {
    if (section.getBoundingClientRect().top < window.innerHeight) continue;
    section.classList.add('nz-reveal');
    observer.observe(section);
  }
}

function initParallax() {
  const layers = [...document.querySelectorAll('[data-nz-parallax]')];
  if (!layers.length || reducedMotion.matches) return;
  const visible = new Set();
  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target);
  });
  layers.forEach((layer) => io.observe(layer));
  let ticking = false;
  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    for (const layer of visible) {
      if (!(layer instanceof HTMLElement)) continue;
      const rect = layer.getBoundingClientRect();
      const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      const strength = Number(layer.dataset.nzParallax) || 60;
      layer.style.setProperty('--nz-py', `${(progress * strength).toFixed(1)}px`);
    }
  };
  // Capture phase: also hears .page-wrapper, which is what scrolls on desktop in Horizon.
  document.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true, capture: true }
  );
  update();
}

/* ---------- Footer: accordions on phones, open columns on computers ---------- */

const footerWide = window.matchMedia('(min-width: 990px)');

function syncFooter() {
  for (const details of document.querySelectorAll('[data-nz-footer-acc]')) {
    if (details instanceof HTMLDetailsElement) details.open = footerWide.matches;
  }
}

footerWide.addEventListener('change', syncFooter);

function init() {
  syncFooter();
  wishlist.sync();
  trackRecent();
  initReveal();
  initParallax();
  watchOfferPrices();
  requireSize();
  // A shopper with an offer clock (running or over) gets the cart's code put right
  if (offerEnd() !== null) syncOffer();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
